import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer die **Personalakte** (`employee-file-dialog`).
 * Gestartet ueber den `<app-dialog-help-button>` im Dialogkopf (Muster wie
 * `ABSENCE_ACCEPT_HELP_TOUR`).
 *
 * Kein `launchContext`: der Dialog oeffnet aus einer datenabhaengigen Zeile der
 * Mitarbeiterliste, es gibt keinen deterministischen Opener-Anker. Die Tour setzt
 * den Dialog also als offen voraus.
 *
 * **Drei Schritte sind bedingt.** „Lohn & Gehalt" und „Zugriffe" existieren nur mit
 * `HasPayrollAccess` bzw. `CanViewAudit`, die Dokumententabelle nur, wenn der gewaehlte
 * Aktenteil Zeilen hat. Fehlt der Anker im DOM, wird der Schritt uebersprungen — eine Tour
 * darf nicht auf eine Flaeche zeigen, die es fuer diesen Nutzer gar nicht gibt (dieselbe
 * Regel wie „nicht sichtbar heisst nicht vorhanden" im Dialog selbst).
 *
 * Keine Cues: ein Spotlight-Bereich pro Schritt, keine Vertonung.
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

/** Ist der Anker gerade im DOM? Grundlage aller `skipIf` hier. */
const missing = (anchor: string): boolean =>
  !document.querySelector(`[onboardingAnchor="${anchor}"]`);

export const EMPLOYEE_FILE_HELP_TOUR: TourConfig = {
  id: TourFeature.EmployeeFileHelp,
  version: 1,
  title: 'EMPLOYEE_FILE_HELP_TOUR_TITLE',
  description: 'EMPLOYEE_FILE_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 105,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'EMPLOYEE_FILE_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'rail',
          anchor: AnchorId.EmployeeFileRail,
          spotlightAnchor: AnchorId.EmployeeFileRail,
          title: 'TUTORIAL.EMPLOYEE_FILE.RAIL.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.RAIL.CONTENT',
          placement: 'right',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'subbar',
          anchor: AnchorId.EmployeeFileSubbar,
          spotlightAnchor: AnchorId.EmployeeFileSubbar,
          title: 'TUTORIAL.EMPLOYEE_FILE.SUBBAR.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.SUBBAR.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'table',
          anchor: AnchorId.EmployeeFileTable,
          spotlightAnchor: AnchorId.EmployeeFileTable,
          title: 'TUTORIAL.EMPLOYEE_FILE.TABLE.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.TABLE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Tabelle rendert nur mit Zeilen; ein leerer Aktenteil zeigt den Leerzustand.
          skipIf: () => missing(AnchorId.EmployeeFileTable),
        },
        {
          id: 'add',
          anchor: AnchorId.EmployeeFileAdd,
          spotlightAnchor: AnchorId.EmployeeFileAdd,
          title: 'TUTORIAL.EMPLOYEE_FILE.ADD.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.ADD.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'export',
          anchor: AnchorId.EmployeeFileExport,
          spotlightAnchor: AnchorId.EmployeeFileExport,
          title: 'TUTORIAL.EMPLOYEE_FILE.EXPORT.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.EXPORT.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'payroll',
          anchor: AnchorId.EmployeeFileTabPayroll,
          spotlightAnchor: AnchorId.EmployeeFileTabPayroll,
          title: 'TUTORIAL.EMPLOYEE_FILE.PAYROLL.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.PAYROLL.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Reiter existiert nur mit `HasPayrollAccess`.
          skipIf: () => missing(AnchorId.EmployeeFileTabPayroll),
        },
        {
          id: 'audit',
          anchor: AnchorId.EmployeeFileTabAudit,
          spotlightAnchor: AnchorId.EmployeeFileTabAudit,
          title: 'TUTORIAL.EMPLOYEE_FILE.AUDIT.TITLE',
          content: 'TUTORIAL.EMPLOYEE_FILE.AUDIT.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Reiter existiert nur mit `CanViewAudit`.
          skipIf: () => missing(AnchorId.EmployeeFileTabAudit),
        },
      ],
    },
  ],
};
